import React, { forwardRef } from 'react';
import './TextField.less';
import './text-inputs.less';
import classNames from 'classnames';
import { IconButton } from '../buttons/IconButton';
import { getHasValidationErrorClass } from './Validation/utils/ValidationCssUtils';
import { ValidationSummary } from 'utils/ValidationUtils';
import { ValidationWrapper, ValidationWrapperCommonProps } from './Validation/ValidationWrapper';
import { useTrackFocus } from './hooks/useTrackFocus';
import { useTabbingContext } from 'contexts/TabbingContext';

interface ITextFieldProps extends React.HTMLProps<HTMLInputElement> {
  value: string | number;
  onChange?: React.EventHandler<React.ChangeEvent<HTMLInputElement>>;
  onClear?: React.MouseEventHandler;
  showClear?: boolean; // TODO enum for showing clear only on hover ?
  disabled?: boolean;
  placeholder?: string;
  validation?: ValidationSummary;
  autoComplete?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
  overrideDefaultClasses?: boolean;
  ProvidedValidationWrapper?:
    | React.FunctionComponent<ValidationWrapperCommonProps>
    | React.ComponentClass<ValidationWrapperCommonProps>;
}

const getClassName = (props: ITextFieldProps, focused: boolean) =>
  classNames(
    'dpc-text-field',
    'dpc-text-input',
    {
      'dpc-text-input-wrapper': !props.onClear,
      'dpc-text-input-wrapper--focused': focused,
      [`${props.className}`]: !!props.className,
    },
    !props.onClear && getHasValidationErrorClass(props.validation)
  );

const getWrapperClassName = (props: ITextFieldProps, focused: boolean) =>
  classNames('dpc-text-input-wrapper', props.className, {
    'dpc-text-input-wrapper--disabled': props.disabled,
    'dpc-text-input-wrapper--focused': focused,
  });

const showClearButton = (props: ITextFieldProps) => props.value && props.showClear;

const getClearClassName = (disabled?: boolean) =>
  classNames('dpc-text-input__clear', 'dpc-text-input__clear--disabled');

/**
 * Text field input component handling a variety of common concerns.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export const TextField = forwardRef((props: ITextFieldProps, ref: React.Ref<HTMLInputElement>) => {
  const {
    autoComplete,
    id,
    value,
    onChange,
    onClear,
    onKeyDown,
    onKeyUp,
    disabled,
    placeholder,
    type,
    validation,
    onBlur,
    onFocus,
    tabIndex: propsTabIndex,
    style,
    className,
    showClear,
    ...otherProps
  } = props;
  const { isFocused, focusTrackingProps } = useTrackFocus({ onBlur, onFocus });
  const tabIndex = useTabbingContext(propsTabIndex);
  const input = (
    <input
      id={id}
      ref={ref}
      // Chrome 63+ no longer respects autoComplete="off" per the spec, so this has to be invalid to do the same job
      autoComplete={autoComplete || 'leave-me-alone'}
      value={value}
      onChange={onChange}
      disabled={disabled}
      readOnly={disabled}
      className={props.overrideDefaultClasses ? props.className : getClassName(props, isFocused)}
      placeholder={placeholder}
      type={type}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      autoFocus={props.autoFocus}
      maxLength={props.maxLength}
      style={style}
      {...otherProps}
      {...focusTrackingProps}
    />
  );
  return !props.onClear ? (
    <ValidationWrapper validation={validation}>{input}</ValidationWrapper>
  ) : (
    <ValidationWrapper validation={validation}>
      <section className={getWrapperClassName(props, isFocused)}>
        {input}
        {showClearButton(props) && (
          <IconButton
            iconSize="small"
            className={getClearClassName(disabled)}
            iconClassName="dpc-text-input__clear-icon"
            onClick={onClear}
            aria-label="Clear"
          />
        )}
      </section>
    </ValidationWrapper>
  );
});
